import { useCallback, useEffect, useState } from 'react';

import { Alert, Box, Button, TextField } from '@mui/material';
import { Spinner } from '@provider-portal/common/components/Spinner/Spinner';
import { validatePassword } from '@provider-portal/common/utils/passwordValidator';

import { NetworkRequestState } from '@bolteu/redux-session';

interface SubmitNewPassword {
  token: string;
  password: string;
}

interface SetNewPasswordProps {
  token: string;
  strings: Record<
    | 'passwordNotMatched'
    | 'passwordMinimumRequirements'
    | 'passwordChanged'
    | 'error'
    | 'password'
    | 'confirmPassword'
    | 'setPassword'
    | 'goToLogin',
    string
  >;
  onSubmit: (payload: SubmitNewPassword) => void;
  onGoBackToLogin: () => void;
  alertMessage: string | null;
  requestState: NetworkRequestState;
}

export function SetNewPasswordForm({
  token,
  strings,
  onSubmit,
  onGoBackToLogin,
  alertMessage,
  requestState,
}: SetNewPasswordProps) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isErrorPassword, setIsErrorPassword] = useState(false);
  const [passwordHelperText, setPasswordHelperText] = useState('');
  const [isErrorConfirmPassword, setIsErrorConfirmPassword] = useState(false);
  const [confirmPasswordHelperText, setConfirmPasswordHelperText] = useState('');
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  const [shouldShowErrorAlert, setShouldShowErrorAlert] = useState(false);
  const [shouldShowSuccessAlert, setShouldShowSuccessAlert] = useState(false);

  const resetFormFieldErrors = useCallback(() => {
    setIsErrorPassword(false);
    setShouldShowErrorAlert(false);
    setPasswordHelperText('');
  }, []);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      const validationResultErrors = validatePassword(password);
      if (!validationResultErrors) {
        onSubmit({ token, password });
        return;
      }

      setIsErrorPassword(true);
      setIsErrorConfirmPassword(true);
      setConfirmPasswordHelperText(strings.passwordMinimumRequirements);
    },
    [onSubmit, password, strings.passwordMinimumRequirements, token],
  );

  const handlePasswordChange = useCallback(
    (e) => {
      setPassword(e.target.value);
      resetFormFieldErrors();
    },
    [resetFormFieldErrors],
  );

  const handleConfirmPasswordChange = useCallback((e) => {
    setConfirmPassword(e.target.value);
  }, []);

  const handleGoBackToLogin = useCallback(() => {
    onGoBackToLogin();
  }, [onGoBackToLogin]);

  useEffect(() => {
    if ((!password && !confirmPassword) || password === confirmPassword) {
      setIsErrorConfirmPassword(false);
      setConfirmPasswordHelperText('');

      if (password) {
        setIsButtonDisabled(false);
      }
    }

    if (confirmPassword && password !== confirmPassword) {
      setIsButtonDisabled(true);
      setIsErrorConfirmPassword(true);
      setConfirmPasswordHelperText(strings.passwordNotMatched);
    }
  }, [password, confirmPassword, strings.passwordNotMatched]);

  useEffect(() => {
    if (alertMessage) {
      setPasswordHelperText(alertMessage);
      setIsErrorPassword(true);
      return;
    }

    if (requestState === NetworkRequestState.failure) {
      setShouldShowErrorAlert(true);
      return;
    }

    if (requestState === NetworkRequestState.success) {
      setShouldShowSuccessAlert(true);
    }

    setIsErrorPassword(false);
    setShouldShowErrorAlert(false);
    setPasswordHelperText('');
  }, [alertMessage, requestState]);

  return (
    <form onSubmit={handleSubmit} data-testid="set-password-form">
      {shouldShowErrorAlert ? (
        <Box mt={1}>
          <Alert variant="filled" severity="error">
            {strings.error}
          </Alert>
        </Box>
      ) : null}
      {shouldShowSuccessAlert ? (
        <>
          <Alert variant="filled" severity="info">
            {strings.passwordChanged}
          </Alert>
          <div>
            <Button variant="contained" color="primary" onClick={handleGoBackToLogin}>
              <span>{strings.goToLogin}</span>
            </Button>
          </div>
        </>
      ) : (
        <>
          <TextField
            error={isErrorPassword}
            helperText={passwordHelperText}
            variant="outlined"
            margin="normal"
            fullWidth
            name="password"
            label={strings.password}
            type="password"
            id="password"
            autoComplete="off"
            onChange={handlePasswordChange}
          />
          <TextField
            error={isErrorConfirmPassword}
            helperText={confirmPasswordHelperText}
            variant="outlined"
            margin="normal"
            fullWidth
            name="confirm_password"
            label={strings.confirmPassword}
            type="password"
            id="confirm_password"
            autoComplete="off"
            onChange={handleConfirmPasswordChange}
          />
          <Box mt={2}>
            <Button
              disabled={isButtonDisabled}
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
            >
              {requestState === NetworkRequestState.waiting ? (
                <Spinner />
              ) : (
                <span>{strings.setPassword}</span>
              )}
            </Button>
          </Box>
        </>
      )}
    </form>
  );
}
